import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BuilderOutput from "../../TemplateBuilder/builder/builder-output";
import { axiosInstance } from "../../../redux/constants/axiosInstance";
import {
  API_URL,
  DEQUEUE_PURCHASE_ORDER_EMAIL_JOB,
} from "../../../redux/constants/type";
import { SendPurchasingOrderSupplier } from "../../../redux/actions/action";
import { handleGeneralNotification } from "../../../utils/helpers/notifications";

const hiddenWorkerStyle = {
  position: "fixed",
  left: "-200vw",
  top: 0,
  width: "800px",
  height: "1200px",
  overflow: "hidden",
  opacity: 0,
  pointerEvents: "none",
  zIndex: -1,
};

const waitForFrame = () =>
  new Promise((resolve) => {
    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      return;
    }

    setTimeout(resolve, 32);
  });

const getDefaultTemplate = (templates) => {
  const nonSystemDefault = templates.filter(
    (item) => item.isSystem === false && item.isdefault === true,
  );

  if (nonSystemDefault.length > 0) {
    return nonSystemDefault[0];
  }

  const systemDefault = templates.filter(
    (item) => item.isSystem === true && item.isdefault === true,
  );

  if (systemDefault.length > 0) {
    return systemDefault[0];
  }

  return templates[0] || null;
};

const PurchaseOrderEmailWorker = () => {
  const dispatch = useDispatch();
  const emailJob = useSelector((state) => state.user.purchaseOrderEmailJob);
  const emailJobQueue = useSelector(
    (state) => state.user.purchaseOrderEmailJobQueue,
  );
  const [builderItems, setBuilderItems] = useState(null);
  const [genrateLink, setGenrateLink] = useState(false);
  const lastSentLinkRef = useRef("");
  const builderInput = useMemo(() => {
    const baseItems =
      builderItems || {
        status: true,
        message: "Purchasing order pdf data",
        data: [],
      };

    return {
      ...baseItems,
      genrateLink: "genrateLink",
    };
  }, [builderItems]);

  useEffect(() => {
    const isBusy = emailJob?.requestId && emailJob.status !== "idle";
    if (isBusy || !Array.isArray(emailJobQueue) || emailJobQueue.length === 0) {
      return;
    }

    const nextJob = emailJobQueue[0];
    if (!nextJob?.requestId) {
      dispatch({ type: DEQUEUE_PURCHASE_ORDER_EMAIL_JOB });
      return;
    }

    dispatch({
      type: "SET_PURCHASE_ORDER_EMAIL_JOB",
      data: {
        ...nextJob,
        status: "preparing",
        error: "",
        pdfLink: "",
      },
    });
    dispatch({ type: DEQUEUE_PURCHASE_ORDER_EMAIL_JOB });
  }, [dispatch, emailJob?.requestId, emailJob?.status, emailJobQueue]);

  useEffect(() => {
    if (!emailJob?.requestId || emailJob.status !== "preparing") {
      return;
    }

    let cancelled = false;
    const run = async () => {
      lastSentLinkRef.current = "";
      setBuilderItems(null);
      setGenrateLink(true);
      dispatch({ type: "SINGLE_UPLOAD_TO_S3", data: [] });

      try {
        dispatch({
          type: "SET_PURCHASE_ORDER_EMAIL_JOB",
          data: { status: "fetching-template", error: "" },
        });

        const templateResponse = await axiosInstance.get(
          `${API_URL}/api/User/FetchTemplates/${"Stock"}`,
        );
        if (cancelled) return;

        const templates = templateResponse.data?.data || [];
        if (templateResponse.data?.status !== true || templates.length === 0) {
          throw new Error("No purchase order template is available.");
        }

        const selectedTemplate = getDefaultTemplate(templates);
        if (!selectedTemplate?.templateId) {
          throw new Error("Could not determine the default purchase order template.");
        }

        dispatch({
          type: "SET_PURCHASE_ORDER_EMAIL_JOB",
          data: {
            status: "rendering",
            templateId: selectedTemplate.templateId,
          },
        });

        const poDocResponse = await axiosInstance.get(
          `${API_URL}/api/PurchasingOrderDoc/${emailJob.orderId}/template/${selectedTemplate.templateId}`,
        );
        if (cancelled) return;

        if (poDocResponse.data?.status === false) {
          throw new Error(
            poDocResponse.data?.message ||
              "Failed to load the purchase order PDF template data.",
          );
        }

        setBuilderItems(poDocResponse.data);
        await waitForFrame();
        if (cancelled) return;

        dispatch({
          type: "SET_PURCHASE_ORDER_EMAIL_JOB",
          data: { status: "uploading" },
        });
        setGenrateLink(true);
      } catch (error) {
        if (cancelled) return;
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to prepare the purchase order email attachment.";
        handleGeneralNotification(
          {
            notificationType: "CUSTOM",
            text: message,
          },
          false,
        );
        dispatch({
          type: "SET_PURCHASE_ORDER_EMAIL_JOB",
          data: { status: "failed", error: message },
        });
        dispatch({ type: "SINGLE_UPLOAD_TO_S3", data: [] });
        setBuilderItems(null);
        setGenrateLink(false);
        dispatch({ type: "RESET_PURCHASE_ORDER_EMAIL_JOB" });
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [dispatch, emailJob?.orderId, emailJob?.requestId]);

  useEffect(() => {
    if (!emailJob?.requestId || emailJob.status !== "sending") {
      return;
    }

    let cancelled = false;

    const sendEmail = async () => {
      try {
        const response = await dispatch(
          SendPurchasingOrderSupplier({
            purchasingOrderId: emailJob.orderId,
            supplierEmail: emailJob.supplierEmail || "",
            link: emailJob.pdfLink || "",
          }),
        );

        if (cancelled) return;

        if (!response || response.status === false) {
          throw new Error(
            response?.message ||
              "The purchase order email could not be sent to the supplier.",
          );
        }

        handleGeneralNotification(
          {
            notificationType: "CUSTOM",
            text: "Purchase order email sent successfully.",
          },
          false,
        );
        dispatch({ type: "SINGLE_UPLOAD_TO_S3", data: [] });
        dispatch({ type: "RESET_PURCHASE_ORDER_EMAIL_JOB" });
        setBuilderItems(null);
        setGenrateLink(false);
        lastSentLinkRef.current = "";
      } catch (error) {
        if (cancelled) return;
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "The purchase order email could not be sent.";
        handleGeneralNotification(
          {
            notificationType: "CUSTOM",
            text: message,
          },
          false,
        );
        dispatch({
          type: "SET_PURCHASE_ORDER_EMAIL_JOB",
          data: { status: "failed", error: message },
        });
        dispatch({ type: "SINGLE_UPLOAD_TO_S3", data: [] });
        setBuilderItems(null);
        setGenrateLink(false);
        dispatch({ type: "RESET_PURCHASE_ORDER_EMAIL_JOB" });
      }
    };

    sendEmail();

    return () => {
      cancelled = true;
    };
  }, [dispatch, emailJob?.pdfLink, emailJob?.requestId, emailJob?.status, emailJob?.orderId, emailJob?.supplierEmail]);

  const handleChildDataLink = (data) => {
    const link = Array.isArray(data) ? data[0] : data;
    if (!link || lastSentLinkRef.current === link) {
      return;
    }

    lastSentLinkRef.current = link;
    dispatch({
      type: "SET_PURCHASE_ORDER_EMAIL_JOB",
      data: {
        status: "sending",
        pdfLink: link,
      },
    });
  };

  if (!emailJob?.requestId) {
    return null;
  }

  return (
    <div style={hiddenWorkerStyle} aria-hidden="true">
      <BuilderOutput
        key={emailJob.requestId}
        type="Stock"
        items={builderInput}
        onRequestData={() => {}}
        genrateLink={genrateLink}
        setGenrateLink={setGenrateLink}
        childDataLink={handleChildDataLink}
        blockPDFDownload={true}
      />
    </div>
  );
};

export default PurchaseOrderEmailWorker;
